import { useMemo, useState } from 'react';
import { Box, Typography, Stack, FormControlLabel, Switch, CircularProgress, TextField, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Arbeitspaket } from '@pm/shared';
import { useArbeitspakete } from '../../../../api/hooks/useArbeitspakete';
import { useProject, useUpdateProject } from '../../../../api/hooks/useProjects';
import { useSyncMaturityFromCount } from '../../../../api/hooks/useReifegrad';
import { useProjectKanbanBoards, unwrapProjectBoards, getRaciPersons } from '../../../../api/hooks/useKanban';
import { KANBAN_MODE_DEFAULT, resolveKanbanMode, type KanbanBoardMode } from '../../../../api/hooks/useKanbanSettings';
import { useNotifications } from '../../../../context/NotificationContext';
import { ApKanbanSection } from './ApKanbanSection';
import { ProjektBoardView } from './ProjektBoardView';
import { PROJEKT_KANBAN_AKTIV } from '../../../../api/hooks/projektKanbanFlags';
import { ProjectKanbanView } from './ProjectKanbanView';
import { apiErrorMessage } from '../../../../lib/apiErrorMessage';

export function KanbanTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { data, isLoading } = useArbeitspakete(projectId);
  const { data: projectData } = useProject(projectId);
  const updateProject = useUpdateProject();
  const [showHidden, setShowHidden] = useState(false);

  // Projekt-Override (kann null sein = erbt Mandanten-Standard).
  const projectMode = (projectData as { data?: { kanban_board_mode?: KanbanBoardMode | null } } | undefined)?.data?.kanban_board_mode ?? null;
  const mode = resolveKanbanMode(projectMode);

  const arbeitspakete = useMemo(() => {
    const list = (data as unknown as { data?: Arbeitspaket[] } | undefined)?.data ?? [];
    // Reihenfolge wie in der AP-Liste: nach nr (natürliche Sortierung).
    return [...list].sort((a, b) => a.nr.localeCompare(b.nr, undefined, { numeric: true, sensitivity: 'base' }));
  }, [data]);

  // Reifegrad Schritt 14 (Kanban) — Proxy = Anzahl Arbeitspakete.
  useSyncMaturityFromCount(projectId, 14, isLoading ? -1 : arbeitspakete.length);

  // Projektweites Board-Aggregat nur im Projekt-Modus laden.
  const { data: projBoardsData, isLoading: projLoading } = useProjectKanbanBoards(projectId, mode === 'per_project');
  const projectBoards = useMemo(() => unwrapProjectBoards(projBoardsData), [projBoardsData]);
  const allAps = useMemo(() => arbeitspakete.map((ap) => ({ id: ap.id, nr: ap.nr, titel: ap.titel })), [arbeitspakete]);
  // Personen-Picker-Optionen (P-14): RACI-Personen (R/A/C/I) aller Arbeitspakete des Projekts.
  const personOptions = useMemo(() => getRaciPersons(arbeitspakete), [arbeitspakete]);

  const handleModeChange = async (value: string) => {
    const next: KanbanBoardMode | null = value === 'inherit' ? null : (value as KanbanBoardMode);
    try {
      await updateProject.mutateAsync({ id: projectId, data: { kanban_board_mode: next } });
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  // PHQ-104: Projekt-Kanban (Aufgaben ohne Arbeitspaket) nur bei aktivem Schalter.
  const projektKanban = PROJEKT_KANBAN_AKTIV && mode === 'per_project';

  const intro = projektKanban
    ? t('kanban.introProjektBoard', 'Ein Board direkt am Projekt, unabhängig von Arbeitspaketen. „Neue Woche" friert das aktuelle Board ein und übernimmt die Karten.')
    : mode === 'per_project'
    ? t('kanban.introProject', 'Ein projektweites Board mit allen Aufgaben aller Arbeitspakete. Jede Karte zeigt ihr Herkunfts-Arbeitspaket.')
    : t('kanban.intro', 'Pro Arbeitspaket ein Bereich mit Boards (neuestes oben). „Neues Board" friert den aktuellen Stand ein und übernimmt die Karten.');

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 260 }}>
          {intro}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            select size="small"
            label={t('kanban.viewMode', 'Darstellung')}
            value={projectMode ?? 'inherit'}
            onChange={(e) => handleModeChange(e.target.value)}
            sx={{ minWidth: 230 }}
          >
            <MenuItem value="inherit">
              {`${t('kanban.modeInherit', 'Standard')} (${t(`kanban.mode_${KANBAN_MODE_DEFAULT}`, KANBAN_MODE_DEFAULT)})`}
            </MenuItem>
            <MenuItem value="per_ap">{t('kanban.mode_per_ap', 'Pro Arbeitspaket')}</MenuItem>
            <MenuItem value="per_project">{t('kanban.mode_per_project', 'Ein Board pro Projekt')}</MenuItem>
          </TextField>
          <FormControlLabel
            control={<Switch size="small" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />}
            label={t('kanban.showHidden', 'Ausgeblendete Karten zeigen')}
          />
        </Stack>
      </Stack>

      {projektKanban ? (
        <ProjektBoardView projectId={projectId} showHidden={showHidden} personOptions={personOptions} />
      ) : arbeitspakete.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          {t('kanban.noAps', 'Keine Arbeitspakete vorhanden. Lege zuerst Arbeitspakete an.')}
        </Typography>
      ) : mode === 'per_project' ? (
        projLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <ProjectKanbanView projectId={projectId} boards={projectBoards} allAps={allAps} showHidden={showHidden} personOptions={personOptions} />
        )
      ) : (
        arbeitspakete.map((ap) => (
          <ApKanbanSection key={ap.id} projectId={projectId} ap={ap} showHidden={showHidden} personOptions={personOptions} />
        ))
      )}
    </Box>
  );
}
